import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowRight, faHistory, faTimes } from '@fortawesome/free-solid-svg-icons';
import SUGGESTIONS from '../../data/searchSuggestions';
import Button from '../ui/Button';

const TRENDING = ['nature', 'business', 'food', 'travel', 'technology', 'abstract'];
const RECENT_KEY = 'pixiy-recent-searches';
const MAX_RECENT = 6;

const loadRecent = () => {
    try {
        return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
    } catch {
        return [];
    }
};

const HeroSearch = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [recent, setRecent] = useState(loadRecent);
    const boxRef = useRef(null);
    const inputRef = useRef(null);

    // Instant, local autocomplete: keywords that contain what the user typed.
    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        return SUGGESTIONS.filter((s) => s.includes(q) && s !== q).slice(0, 6);
    }, [query]);

    const isRecentList = !query.trim();
    const items = isRecentList ? recent : matches;

    // Close the dropdown when clicking outside the search box.
    useEffect(() => {
        const onClick = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    // Reset the active highlight whenever the visible list changes.
    useEffect(() => setActive(-1), [query, open]);

    const persistRecent = (term) => {
        const next = [term, ...recent.filter((r) => r !== term)].slice(0, MAX_RECENT);
        setRecent(next);
        try {
            localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        } catch {
            /* ignore quota / private-mode errors */
        }
    };

    const go = (term) => {
        const t = (term || '').trim();
        if (!t) return;
        persistRecent(t);
        setQuery(t);
        setOpen(false);
        inputRef.current?.blur();
        navigate(`/photos?q=${encodeURIComponent(t)}`);
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) return setOpen(true);
            setActive((a) => Math.min(a + 1, items.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            go(active >= 0 && items[active] ? items[active] : query);
        } else if (e.key === 'Escape') {
            setOpen(false);
            setActive(-1);
        }
    };

    const clearRecent = () => {
        setRecent([]);
        try {
            localStorage.removeItem(RECENT_KEY);
        } catch {
            /* ignore */
        }
    };

    // Highlight the matching fragment inside a suggestion.
    const renderLabel = (text) => {
        const q = query.trim().toLowerCase();
        const i = q ? text.toLowerCase().indexOf(q) : -1;
        if (i === -1) return text;
        return (
            <>
                {text.slice(0, i)}
                <span className="font-bold text-brand-600 dark:text-brand-400">{text.slice(i, i + q.length)}</span>
                {text.slice(i + q.length)}
            </>
        );
    };

    const showDropdown = open && items.length > 0;

    return (
        <div className="w-full max-w-lg mx-auto lg:mx-0">
            <div ref={boxRef} className="relative">
                {/* input */}
                <div
                    className={`flex items-center gap-3 bg-white dark:bg-gray-800 border rounded-2xl px-4 py-1.5 shadow-e0 transition-all ${
                        open ? 'border-brand-400 ring-2 ring-brand-400/40' : 'border-gray-200 dark:border-gray-700'
                    }`}
                >
                    <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                    {/* focus:outline-none is deliberate here: the wrapper above
                        rings the whole pill while `open`, so an outline on the
                        bare input would draw a second indicator inside it. */}
                    <input
                        ref={inputRef}
                        type="text"
                        role="combobox"
                        aria-label="Search free photos, illustrations and videos"
                        aria-expanded={showDropdown}
                        aria-controls="hero-search-listbox"
                        aria-autocomplete="list"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        onKeyDown={onKeyDown}
                        placeholder="Search free photos, illustrations & videos…"
                        className="flex-1 min-w-0 min-h-[44px] bg-transparent border-none text-gray-800 dark:text-gray-100 dark:placeholder-gray-500 focus:outline-none leading-tight"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => {
                                setQuery('');
                                inputRef.current?.focus();
                            }}
                            aria-label="Clear search"
                            className="tap-target inline-flex items-center justify-center h-8 w-8 shrink-0 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                    <Button
                        size="icon"
                        onClick={() => go(query)}
                        aria-label="Search"
                        className="hidden sm:inline-flex"
                    >
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                </div>

                {/* autocomplete dropdown */}
                {showDropdown && (
                    <ul
                        id="hero-search-listbox"
                        role="listbox"
                        className="absolute z-30 left-0 right-0 mt-2 py-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-e2 overflow-hidden text-left"
                    >
                        {isRecentList && (
                            <li className="flex items-center justify-between px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                <span>Recent</span>
                                <button
                                    type="button"
                                    onClick={clearRecent}
                                    className="tap-target inline-flex items-center px-2 py-1 rounded-full hover:text-brand-600 dark:hover:text-brand-400 normal-case font-medium"
                                >
                                    Clear
                                </button>
                            </li>
                        )}
                        {items.map((item, i) => (
                            <li key={item} role="option" aria-selected={active === i}>
                                <button
                                    type="button"
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => go(item)}
                                    className={`w-full min-h-[44px] flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-brand-50 dark:hover:bg-gray-700/60 ${
                                        active === i ? 'bg-brand-50 dark:bg-gray-700/60' : ''
                                    }`}
                                >
                                    <FontAwesomeIcon
                                        icon={isRecentList ? faHistory : faSearch}
                                        className="text-gray-400 text-sm"
                                    />
                                    <span className="capitalize text-gray-700 dark:text-gray-200">{renderLabel(item)}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* trending chips */}
            <div className="mt-4 flex flex-wrap items-center justify-center lg:justify-start gap-x-2 gap-y-4">
                <span className="text-sm text-gray-400">Trending:</span>
                {TRENDING.map((t) => (
                    <button
                        key={t}
                        type="button"
                        onClick={() => go(t)}
                        className="tap-target capitalize text-sm px-3.5 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-brand-500 hover:text-white transition-colors"
                    >
                        {t}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default HeroSearch;
